import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { X, Plus, Upload, Link, Cloud } from 'lucide-react';
import { theme } from '../styles/theme';
import { FormData } from '../types';
import TabNavigation, { Tab } from './TabNavigation';
import URLUploadTab from './URLUploadTab';
import FileUploadTab from './FileUploadTab';
import CloudAppsTab from './CloudAppsTab';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: ${theme.spacing[4]};
`;

const ModalContainer = styled.div`
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.xl};
  box-shadow: ${theme.shadows.xl};
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

const ModalHeader = styled.div`
  padding: ${theme.spacing[6]};
  border-bottom: 1px solid ${theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ModalTitle = styled.h2`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  font-family: ${theme.typography.fontFamily.display};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
`;

const ModalDescription = styled.p`
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.fontSize.sm};
  margin-top: ${theme.spacing[2]};
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
  color: ${theme.colors.textSecondary};
  cursor: pointer;
  border-radius: ${theme.borderRadius.md};
  transition: all ${theme.transitions.base};

  &:hover {
    background: ${theme.colors.primary[100]};
    color: ${theme.colors.primary[600]};
  }
`;

const ModalContent = styled.div`
  padding: ${theme.spacing[6]};
`;

interface NewAnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: FormData) => void;
  isLoading?: boolean;
}

const NewAnalysisModal: React.FC<NewAnalysisModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false
}) => {
  const [activeTab, setActiveTab] = useState('file');

  const tabs: Tab[] = [
    {
      id: 'file',
      label: 'File Upload',
      icon: <Upload size={18} />
    },
    {
      id: 'url',
      label: 'URL Upload',
      icon: <Link size={18} />
    },
    {
      id: 'cloud',
      label: 'Cloud Apps',
      icon: <Cloud size={18} />,
      badge: 'Soon'
    }
  ];
  // Close modal on escape key press
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Close modal when clicking on overlay
  const handleOverlayClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer>
        <ModalHeader>
          <div>
            <ModalTitle>
              <Plus size={24} />
              New Tender Response
            </ModalTitle>
            <ModalDescription>
              Upload your tender document to generate or refine responses using AI.
            </ModalDescription>
          </div>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>
        
        <ModalContent>
          <TabNavigation
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />
          
          {activeTab === 'file' && (
            <FileUploadTab 
              onSubmit={onSubmit}
              isLoading={isLoading}
            />
          )}
          
          {activeTab === 'url' && (
            <URLUploadTab 
              onSubmit={onSubmit}
              isLoading={isLoading}
            />
          )}
          
          {activeTab === 'cloud' && (
            <CloudAppsTab />
          )}
        </ModalContent>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default NewAnalysisModal;